import styled from '@emotion/styled'
import {FC, forwardRef, ForwardRefRenderFunction, RefObject, useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {GridStateSnapshot, VirtuosoGrid, VirtuosoHandle} from 'react-virtuoso'
import {clearFavorites, GetFavorites, useFavoriteCursor, useFavoriteList} from 'entities/activity'
import {GetSearchPeople, useGetSearchCursor, useGetSearchPeople} from 'entities/searchPeople'
import {NoResults} from 'features/noResults'
import {UserCard} from 'features/userCard'
import {names} from 'shared/lib'
import {SearchUser} from 'shared/types'
import {Loader} from 'shared/ui/loader'
import styles from './styles.module.scss'

//TODO pass list & cursor & get function if it needed
type VirtualizedUserListProps = {isPeoplePage?: boolean}


const scrollState = {
  peoplePage: 0,
  favoritePage: 0
}
let gridSnapshot: GridStateSnapshot | undefined

const List: ForwardRefRenderFunction<HTMLDivElement, VirtualizedUserListProps> = ({isPeoplePage}, wrapperRef) => {
  const virtuosoRef = useRef<VirtuosoHandle | null>(null)
  const [restoreState, setRestoreState] = useState<GridStateSnapshot | null>(null)
  const dispatch = useDispatch()
  const list = isPeoplePage ? useGetSearchPeople() : useFavoriteList()
  const cursor = isPeoplePage ? useGetSearchCursor() : useFavoriteCursor()
  const {isFetching, refetch, isError} = isPeoplePage ? GetSearchPeople(dispatch) : GetFavorites({cursor}, dispatch)
  const scrollKey = isPeoplePage ? 'peoplePage' : 'favoritePage'
  const favoriteList = useFavoriteList()

  useEffect(() => {
    setTimeout(() => {
      if (scrollState[scrollKey] >= 200) virtuosoRef.current?.scrollTo({top: scrollState[scrollKey]})
      if (scrollState[scrollKey] < 200) (wrapperRef as RefObject<HTMLDivElement>)?.current?.scrollTo({top: 0})
    }, 100)

    if (isFetching || isError || cursor == null) return

    refetch().catch(console.error)
  }, [wrapperRef])


  useEffect(() => {
    if (isFetching || cursor !== '') return

    refetch().catch(console.error)
  }, [cursor, isFetching])

  useEffect(() => {
    return () => {
      if (!gridSnapshot) return

      setRestoreState(gridSnapshot)
      //crutch for start of block
      scrollState[scrollKey] = gridSnapshot.scrollTop > 0 ? gridSnapshot.scrollTop : scrollState[scrollKey]
      if (isPeoplePage) return
      //if favorite page clear state on unmount
      dispatch(clearFavorites())
    }
  }, [])

  return (
    <>
      {isError ? 'Error' : null}
      {!(list.length || isFetching) ? <NoResults
        className={names(styles.noResult, isPeoplePage && styles.margin)}
        header='No users found'
        text='There are no users here yet'
      /> : <div className={names(styles.wrapper, isPeoplePage && styles.margin)}>
        <VirtuosoGrid
          ref={virtuosoRef}
          customScrollParent={(wrapperRef as RefObject<HTMLDivElement>)?.current as HTMLElement}
          context={{isFetching, refetch}}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
          components={{
            List: forwardRef((props, ref) =>
              <div ref={ref} {...props} className={`${props.className} ${styles.cardWrapper}`} />),
            Item: styled.div`min-width: 100px`
          }}
          restoreStateFrom={restoreState}
          data={list}
          overscan={200}
          stateChanged={(state) => gridSnapshot = state}
          endReached={() => cursor !== null && refetch()}
          itemContent={(_, trustedUser) =>
            itemContent({
              trustedUser,
              isPeoplePage,
              canHideFromList: !isPeoplePage
            })
          }
        />
        {isFetching ? <div className={styles.loader}><Loader /></div> : null}
      </div>}
    </>
  )
}

const itemContent: FC<{
  trustedUser: SearchUser
  isPeoplePage?: boolean
  canHideFromList?: boolean
}> = ({trustedUser, isPeoplePage, canHideFromList}) => {
  return (
    <UserCard
      key={trustedUser.id}
      trustedUser={trustedUser}
      isFavorite={!isPeoplePage}
      withChatNowBtn
      canHideFromList={canHideFromList}
    />
  )
}
export const VirtualizedUserList = forwardRef(List)